<template>
  <v-app class="theme--nif">
    <header-block>
      <template #title>
        <router-link :to="'/start'">
          <v-img
            id="io-logo-sm"
            class="d-none d-sm-block"
            src="@/assets/IO.svg"
            :alt="$t('appLayoutStrings.toolbarTitle')"
            width="300"
            height="70"
          />
          <v-img
            id="io-logo"
            class="d-block d-sm-none"
            src="@/assets/io-logo.svg"
            :alt="$t('appLayoutStrings.toolbarTitle')"
            width="58"
            height="70"
          />
        </router-link>
      </template>
      <template #inside-right>
        <context-switch
          :orgs="orgsWithPermissions"
          :name="name"
        />
      </template>
      <template #menu>
        <app-menu
          :current-org="currentOrgWithPermissions"
        />
      </template>
    </header-block>

    <v-overlay :model-value="loading">
      <v-progress-circular
        indeterminate
        size="64"
      />
    </v-overlay>

    <v-main
      id="mainContent"
      :class="[{'image-title-background': useImageTitleStyle}]"
    >
      <welcome-banner v-if="useImageTitleStyle" />
      <v-container :class="['py-10', 'main-container', {'rounded-content': !mobile}]">
        <info-message-alerts :current-org="currentOrgWithPermissions" />
        <slot />
        <help-bar />
      </v-container>
      <app-footer />
    </v-main>
  </v-app>
</template>

<script setup>
import { useStore } from "vuex";
import InfoMessageAlerts from "./components/InfoMessageAlerts";
import ContextSwitch from "./components/ContextSwitch";
import HelpBar from "./components/help/HelpBar";
import AppMenu from "./components/AppMenu";
import WelcomeBanner from './components/WelcomeBanner';
import AppFooter from './components/AppFooter';
import HeaderBlock from "@/views/common/components/HeaderBlock";
import { useDisplay } from 'vuetify';
import { ref, computed, watch } from 'vue';
import { useOidc } from '@/composables/oidc';

const loading = ref(true);

const props = defineProps({
  styleOverride: String
});

const { mobile } = useDisplay();

const store = useStore();
const oidc = useOidc();

const user = computed(() => oidc.user);
const currentOrgId = computed(() => store.getters["contextStore/orgId"]);
const orgsWithPermissions = computed(() => store.getters["currentOrgsStore/orgs"]);
const contextInitiated = computed(() => store.getters["contextStore/initiated"]);

const name = computed(() => `${user.value?.given_name} ${user.value?.family_name}`);

const currentOrgWithPermissions = computed(() => {
  const org = orgsWithPermissions.value.find(
    x => x.org.id === currentOrgId.value
  );

  return org || null;
});

const useImageTitleStyle = computed(() => {
  return props.styleOverride === 'image-title';
});

watch(() => contextInitiated,
  val => {
    if (val) {
      loading.value = false;
    }
  },
  { immediate: true });
</script>

<style lang="scss">
.main-container {
  min-height: calc(100vh - 312px);
}

.v-alert--prominent {
  .v-icon{
    margin-left: 8px;
  }
}

.v-expansion-panel-title,
.v-btn,
.v-input input,
a {
  &:focus {
    outline: 1px dotted #dddddd;
  }
}

.skip-to-content-link {
  position: absolute;
  left: -2000px;
  font-size: 14px;
  padding: 2.5rem 1rem;

  &:focus {
    left: 0px;
    color: #000;
    background-color: #fff;
  }
}

.white-space-nowrap {
  white-space: nowrap;
}

.v-dialog {
  max-height: 90vh;
  overflow-y: hidden;

  .v-card.v-sheet {
    .v-card__text {
      max-height: calc(90vh - 164px); //subtract height of title + actions, probably brittle if more content is added.
      overflow-y: auto;
    }

    .v-card__title {
      background-color: rgb(var(--v-theme-primary-variant-base));
      color: var(--v-inverted-base);
    }
  }
}

.theme--nif {
  .v-messages.theme--light {
    &.v-messages__wrapper {
      &.v-messages__message {
        opacity: 0.7;
      }
    }
  }

  .large-comment-bubble {
    width: 60%;
  }
}
.v-data-table-header.v-data-table-header-mobile {
  display: none;
}

.v-data-table__mobile-row:nth-of-type(1) {
  margin-top: 1rem;
}

.v-data-table__mobile-table-row {
  .v-data-table__mobile-row {
    padding: 0 !important;
    height: auto !important;
    display: block;
    float: left;
    width: 100%;

    .v-data-table__mobile-row__cell {
      display: block;
      text-align: left !important;
    }
  }
}

.text-h1,
.text-h2,
.text-h3 {
  word-break: break-word;
}
</style>
